// src/components/exercises/ExerciseGoals.jsx
import React, { useEffect, useState } from "react";
import { User, UserProgress } from "@/api/entities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { saveToR2 } from "@/api/r2"; // ✅ AGGIUNTA

// 12 traguardi predefiniti
const GOALS = [
  { key: "1d",  label: "1 giorno senza fumare" },
  { key: "3d",  label: "3 giorni senza fumare" },
  { key: "1w",  label: "1 settimana" },
  { key: "2w",  label: "2 settimane" },
  { key: "3w",  label: "3 settimane" },
  { key: "1m",  label: "1 mese" },
  { key: "2m",  label: "2 mesi" },
  { key: "6m",  label: "6 mesi" },
  { key: "1y",  label: "1 anno" },
  { key: "2y",  label: "2 anni" },
  { key: "5y",  label: "5 anni" },
  { key: "10y", label: "10 anni" },
];

// helper robusti
const safeJsonParse = (v, d = {}) => {
  if (!v) return d;
  try { return typeof v === "string" ? JSON.parse(v) : v; } catch { return d; }
};

export default function ExerciseGoals({ lesson, onClose }) {
  const [rewards, setRewards] = useState({});
  const [saving, setSaving] = useState(false);

  // ✅ usa l'ID stringa della lezione (fallback all'URL)
  const lessonKey = String(
    lesson?.id || new URLSearchParams(location.search).get("id") || "6"
  );

  // carico eventuali risposte già salvate
  useEffect(() => {
    (async () => {
      try {
        const u = await User.me().catch(() => null);
        const created_by = (u?.email || u?.id || u?.uid) ?? undefined;

        let rec = null;
        if (created_by) {
          // prima prova: filtro direttamente per created_by + lesson_id
          try {
            const res = await UserProgress.filter({ created_by, lesson_id: lessonKey });
            rec = Array.isArray(res) ? res[0] : null;
          } catch {}

          // fallback: prendo tutti i record dell'utente e ne cerco uno con lo stesso lesson_id
          if (!rec) {
            try {
              const resAll = await UserProgress.filter({ created_by });
              rec = Array.isArray(resAll)
                ? resAll.find(r => String(r.lesson_id ?? r.lessonId ?? r.lesson) === lessonKey)
                : null;
            } catch {}
          }
        }

        if (rec) {
          const map =
            safeJsonParse(rec.exercise_rewards) ||
            safeJsonParse(rec.rewards_map) ||
            safeJsonParse(rec.rewards_json) ||
            safeJsonParse(rec.exercise_answer) || // spesso è qui
            {};
          setRewards(map);
        }
      } catch {}
    })();
  }, [lessonKey]);

  const setValue = (k, v) => setRewards(prev => ({ ...prev, [k]: v }));

  async function save() {
    setSaving(true);
    try {
      const u = await User.me().catch(() => null);
      const created_by = (u?.email || u?.id || u?.uid) ?? "anon";

      // prova a trovare record esistente della stessa lezione
      let existing = null;
      try {
        const res = await UserProgress.filter({ created_by, lesson_id: lessonKey });
        existing = Array.isArray(res) ? res[0] : null;
      } catch {}
      if (!existing) {
        try {
          const resAll = await UserProgress.filter({ created_by });
          existing = Array.isArray(resAll)
            ? resAll.find(r => String(r.lesson_id ?? r.lessonId ?? r.lesson) === lessonKey)
            : null;
        } catch {}
      }

      // ✅ payload allineato al resto dell’app
      const payload = {
        lesson_id: lessonKey,
        lesson_completed: true,
        exercise_completed: true,
        exercise_type: "goals_rewards",
        exercise_answer: JSON.stringify(rewards), // campo già usato altrove
        exercise_rewards: JSON.stringify(rewards), // alias utile
        rewards_map: rewards,                      // alias utile
        updated_at: new Date().toISOString(),
        created_by,
      };

      // create/update “best effort”
      if (existing?.id && typeof UserProgress?.update === "function") {
        await UserProgress.update(existing.id, payload);
      } else if (typeof UserProgress?.create === "function") {
        await UserProgress.create(payload);
      }

      // ✅ Persisti anche su R2 (best-effort, offline-queue se necessario)
      try {
        await saveToR2("exercise_result", {
          lesson_id: lessonKey,
          data: {
            lesson_id: lessonKey,
            lesson_title: lesson?.title,
            exercise_type: "goals_rewards",
            rewards_map: rewards,
            completion_date: new Date().toISOString(),
          },
          meta: { source: "exercise_goals" },
        });
      } catch (e) {
        console.warn("[R2] exercise_result queued/offline", e);
      }

      // Notifica le altre pagine (Progress.jsx / Lesson.jsx ascoltano “rb:checkin-saved”)
      window.dispatchEvent(new Event("rb:checkin-saved"));
      // chiudi il modal al termine
      onClose?.();
    } finally {
      setSaving(false);
    }
  }

  return (
    // 🔧 overlay stile modal, con chiusura sicura su mousedown solo sull'overlay
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      tabIndex={-1}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header semplice */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Esercizio</h2>
              <p className="text-gray-600">{lesson?.title ?? "Obiettivi & Ricompense"}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => onClose?.()}>Chiudi</Button>
          </div>

          {/* --- CONTENUTO ORIGINALE INVARIATO --- */}
          <div className="space-y-6">
            <p className="text-gray-700">
              Scegli una <b>ricompensa</b> per ciascun traguardo. Quando raggiungerai il traguardo nel
              calendario Progressi, la tua ricompensa apparirà automaticamente nel dettaglio del giorno.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {GOALS.map(g => (
                <div key={g.key} className="space-y-2">
                  <Label>{g.label}</Label>
                  <Input
                    placeholder="Es. Bistecca Wagyu, spa, cinema con amici…"
                    value={rewards[g.key] ?? ""}
                    onChange={e => setValue(g.key, e.target.value)}
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              <Button type="button" onClick={save} disabled={saving}>
                {saving ? "Salvataggio…" : "Salva ricompense"}
              </Button>
            </div>
          </div>
          {/* --- /contenuto --- */}
        </div>
      </div>
    </div>
  );
}
